import {
  EnvironmentLogic,
  ExpressionLogic,
  StatementLogic,
  type Environment,
  type Statement,
  type TemplatePart,
  type TokenPart,
} from '@/modules/form/lang';
import { ComposeLogic, FormLogic, SlotLogic } from '@/modules/form';
import type { SharingFormSlot } from '@/modules/form';
import type { SharingModeGate } from '@/modules/template/types';
import type { Topic } from '@/modules/topic/types';
import { TopicTypeDefLogic } from '../topic_type_def';
import newsDefaultShareFormYaml from './default_share_form.yaml?raw';

/**
 * The news article as a news topic's sharing form lays it out, lifted out of
 * the form's intro so the news welcome sheet can show the story the way the
 * text message did. It is found as two regions.
 *
 * The headline is the first token reading the headline. Its presence is all
 * the sheet needs: it shows the topic's headline in its own heading, cased as
 * the author wrote it rather than however the message dressed it up
 * (`uppercase(_headline)`, say), so the headline is never printed from the
 * form.
 *
 * The article region is printed from the form. It ends at the first token
 * reading the article and reaches back from there to the nearest token
 * reading each of the byline, the dateline place and the dateline date,
 * stopping once all three are found. A form that leaves some of them out
 * still has a region: it starts at the earliest of those it does print, or at
 * the article itself. The spaces and tabs that indent the region's first
 * token are part of it, so an indented byline stays indented; the line breaks
 * around it are not. The search never reaches back past the headline, which
 * is its own region, or across a slot the sender answers, which the welcome
 * sheet cannot know the answer to.
 *
 * Each part is found by the share primitive it is read from or any name the
 * news type's `definitions.yaml` defines as that primitive (see
 * `ARTICLE_PART_ALIASES`): a news topic defines `_headline` as `_title`,
 * `_datelineLocation` as `_location` and `_datelineDate` as `_startDate`, so a
 * form may print either.
 */

/**
 * The parts of a news article, each by the share primitives it is read from.
 * A part's key names its primitive, and the name news gives it where that
 * differs, so the key stays put whatever `definitions.yaml` calls it.
 */
const ARTICLE_PART_PRIMITIVES = {
  titleAkaHeadline: ['_title'],
  byline: ['_byline'],
  locationAkaDatelineLocation: ['_location'],
  startDateAkaDatelineDate: ['_startDate', '_startDatetime'],
  bodyAkaArticle: ['_body'],
} as const;

type ArticlePart = keyof typeof ARTICLE_PART_PRIMITIVES;

/**
 * `primitives` and every name the news type's `definitions.yaml` defines as
 * one of them, or as one of those names in turn. The defines run in order, so
 * one pass sees a name defined from another defined above it.
 */
function aliasesOf(primitives: readonly string[]): readonly string[] {
  const names = new Set(primitives);
  for (const define of TopicTypeDefLogic.definitionsOf('news')) {
    if (define.expr.kind === 'variable' && names.has(define.expr.name)) {
      names.add(define.name);
    }
  }
  return Array.from(names);
}

/** Each part of a news article by every name that prints it. */
const ARTICLE_PART_ALIASES = Object.fromEntries(
  Object.entries(ARTICLE_PART_PRIMITIVES).map(([part, primitives]) => [
    part,
    aliasesOf(primitives),
  ])
) as Record<ArticlePart, readonly string[]>;

/** The parts the article region reaches back to from the article. */
const LEAD_PARTS: readonly ArticlePart[] = [
  'byline',
  'locationAkaDatelineLocation',
  'startDateAkaDatelineDate',
];

/** The two regions of a news article in a form, as found (see above). */
export interface ArticleRegions<H, A> {
  /** The headline, or null when the form never prints it. */
  readonly headline: H | null;
  /** The byline, dateline and article, or null when the form never prints the article. */
  readonly articleWithBylineDateline: A | null;
}

/**
 * A form's printed text as one run of template parts, in body order. A slot
 * that is not a Text slot, or one left out of the message, stands as a `barrier`
 * the article region cannot reach across.
 */
type FlatPart = TemplatePart | { readonly kind: 'barrier' };

function flatten(form: readonly SharingFormSlot[]): readonly FlatPart[] {
  return form.flatMap((slot): readonly FlatPart[] => {
    if (slot.type !== 'text' || !SlotLogic.emitsInMessage(slot)) {
      return [{ kind: 'barrier' }];
    }
    return ExpressionLogic.template(slot.expr, 'asWritten').parts;
  });
}

/** Whether `part` is a token reading any of `articlePart`'s names. */
function reads(part: FlatPart, articlePart: ArticlePart): boolean {
  if (part.kind !== 'token') return false;
  const aliases = ARTICLE_PART_ALIASES[articlePart];
  return ExpressionLogic.freeNames(part.expression).some(name =>
    aliases.includes(name)
  );
}

/**
 * The spaces and tabs that end `text` when they open a line: after its last
 * line break, or all of it when `text` opens the form. A space between words
 * is not an indent.
 */
function indentOf(text: string, opensForm: boolean): string {
  const match = /(^|\n)([^\S\r\n]*)$/.exec(text);
  if (!match || (match[1] === '' && !opensForm)) return '';
  return match[2];
}

/** The two regions of `form` (see above) as the template parts they print. */
function regions(
  form: readonly SharingFormSlot[]
): ArticleRegions<TokenPart, readonly TemplatePart[]> {
  const parts = flatten(form);
  const headlineAt = parts.findIndex(part => reads(part, 'titleAkaHeadline'));
  const headline = headlineAt === -1 ? null : (parts[headlineAt] as TokenPart);
  const end = parts.findIndex(part => reads(part, 'bodyAkaArticle'));
  if (end === -1) return { headline, articleWithBylineDateline: null };

  let start = end;
  const found = new Set<ArticlePart>();
  for (let i = end - 1; i > headlineAt && found.size < LEAD_PARTS.length; i--) {
    const part = parts[i];
    if (part.kind === 'barrier') break;
    for (const lead of LEAD_PARTS) {
      if (!found.has(lead) && reads(part, lead)) {
        found.add(lead);
        start = i;
      }
    }
  }

  const before = parts[start - 1];
  const indent =
    before?.kind === 'literal' ? indentOf(before.text, start === 1) : '';
  const inside = parts.slice(start, end + 1) as readonly TemplatePart[];
  return {
    headline,
    articleWithBylineDateline: indent
      ? [{ kind: 'literal', text: indent }, ...inside]
      : inside,
  };
}

/**
 * The two regions of `form` as statements: the headline as the one emit of
 * its token's expression, and the article region as an emit of each literal
 * run, quoted, and of each token's expression.
 */
function statements(
  form: readonly SharingFormSlot[]
): ArticleRegions<Statement, readonly Statement[]> {
  const { headline, articleWithBylineDateline } = regions(form);
  return {
    headline: headline && { kind: 'emit', expr: headline.expression },
    articleWithBylineDateline:
      articleWithBylineDateline &&
      articleWithBylineDateline.flatMap((part): Statement[] => {
        switch (part.kind) {
          case 'literal':
            return [{ kind: 'emit', expr: ExpressionLogic.quote(part.text) }];
          case 'token':
            return [{ kind: 'emit', expr: part.expression }];
          case 'malformed':
            return [];
        }
      }),
  };
}

/**
 * What `statements` print in `env`, spaced as a message body is (see
 * `TemplateLogic.tidyBody`), except that a leading indent is kept: no line
 * breaks before or after it, and no blank line in it more than one deep.
 */
function print(statements: readonly Statement[], env: Environment): string {
  let text = '';
  StatementLogic.run(statements, env, (expr, at) => {
    text += ExpressionLogic.print(expr, at);
  });
  return ExpressionLogic.tidyText(text)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+/, '')
    .trimEnd();
}

/**
 * The intro form a news topic's messages compose from: its saved form when it
 * parses, else the type's default, the same rule the share wizard follows.
 */
function introForm(topic: Topic): readonly SharingFormSlot[] | null {
  const custom = topic.customTemplates;
  const source =
    custom && FormLogic.isSharingConfig(custom, 'news')
      ? custom
      : newsDefaultShareFormYaml;
  try {
    const config = FormLogic.parseSharingConfigRaw(source, 'news');
    return ComposeLogic.formForIntent(config, 'intro');
  } catch {
    return null;
  }
}

/**
 * The news article of `topic` as the intro message in `sharingMode` shows
 * it: the topic's headline when the message prints one, and the article
 * region as the message printed it. The headline keeps the casing the author
 * gave it rather than what the message's program made of it. Either is null
 * when the message does not carry it, and the article also when it cannot be
 * printed.
 */
function extract(
  topic: Topic,
  sharingMode: SharingModeGate,
  now: Date = new Date()
): ArticleRegions<string, string> {
  const form = introForm(topic);
  if (!form) return { headline: null, articleWithBylineDateline: null };
  const program = statements(
    form.filter(slot =>
      SlotLogic.isCompatibleWithSharingMode(slot, sharingMode)
    )
  );
  // The headline token only says that the message has a headline; what it
  // printed (in capitals, say) gives way to the title as the author cased it.
  const headline = (program.headline && topic.title.trim()) || null;
  if (!program.articleWithBylineDateline) {
    return { headline, articleWithBylineDateline: null };
  }
  try {
    const env = EnvironmentLogic.shareEnvironment({
      topic,
      now,
      profileName: '',
      isUpdateFlow: false,
    });
    return {
      headline,
      articleWithBylineDateline:
        print(program.articleWithBylineDateline, env) || null,
    };
  } catch {
    return { headline, articleWithBylineDateline: null };
  }
}

export const HeadlineAndArticleExtractor = {
  regions,
  statements,
  print,
  extract,
};
